import {
  BreakpointsMap,
  Button as MauiButton,
  constants,
  Icon,
  Text,
} from "@findemdev/maui";
import styled from "styled-components";
import {
  DeliverableGridFrame,
  RAAS_DELIVERABLE_CONTENT_MAX_WIDTH,
} from "./components/DeliverableLayout/DeliverableLayout.styled";
import { MilestonesPaneView } from "./components/MilestonesPane/types";
import {
  RAAS_SPLIT_PANE_ARTIFACT_MIN_WIDTH,
  RAAS_SPLIT_PANE_CHAT_MIN_WIDTH,
  RAAS_SPLIT_PANE_RESIZE_HANDLE_WIDTH,
  SplitPaneResizeHandle,
} from "./deliverables/renderers/SplitPaneResizeHandle.styled";
import { RaasProjectToolbarPlacement } from "./types";

const { colors, dimensions, typography } = constants;

const MILESTONES_COLLAPSED_COLUMN = "70px";
const MILESTONES_EXPANDED_COLUMN = "clamp(220px, 16.32vw, 235px)";
const PROJECT_CONTENT_COLUMN = "minmax(0, 1fr)";
const GLOBAL_ARTIFACT_MOBILE_ROW = "minmax(320px, 45vh)";
const CHAT_WITH_ARTIFACT_HORIZONTAL_PADDING = "32px";
const ARTIFACT_EMPTY_MIN_HEIGHT = "180px";
const DENSE_CONTROL_PADDING = "2px";
const COMPACT_SUBTITLE_FONT_SIZE = "15px";
const ARTIFACT_TITLE_FONT_SIZE = "24px";
const ARTIFACT_TITLE_LINE_HEIGHT = dimensions.spacing["4"];
const ARTIFACT_HEADING_FONT_SIZE = "22px";
const ARTIFACT_HEADING_LINE_HEIGHT = dimensions.height.sm;
const ACTION_BUTTON_SHADOW = "0 1px 2px rgba(16, 24, 40, 0.05)";
const ARTIFACT_CARD_SHADOW = "0 8px 28px rgba(17, 24, 39, 0.08)";
const PREVIEW_HEADER_BACKGROUND = "rgba(225, 239, 255, 0.5)";
const PREVIEW_SURFACE_BACKGROUND = `linear-gradient(
  180deg,
  ${PREVIEW_HEADER_BACKGROUND} 0,
  ${PREVIEW_HEADER_BACKGROUND} 48px,
  rgba(225, 239, 255, 0.26) 72px,
  rgba(225, 239, 255, 0.08) 96px,
  rgba(255, 255, 255, 0) 122px
)`;
const PREVIEW_HEADER_VERTICAL_PADDING = "14px";
const PREVIEW_TITLE_FONT_SIZE = "18px";
const PREVIEW_TITLE_LINE_HEIGHT = "24px";
const PREVIEW_STATUS_FONT_SIZE = "12px";
const PREVIEW_STATUS_LINE_HEIGHT = "20px";
const PREVIEW_STATUS_MIN_HEIGHT = "22px";
const ACCESS_DENIED_MAX_WIDTH = "520px";

export const ProjectWorkspaceGrid = styled.main<{
  $milestonesPaneView: MilestonesPaneView;
}>`
  background: ${colors.white};
  display: grid;
  grid-template-columns: ${({ $milestonesPaneView }) =>
    $milestonesPaneView === MilestonesPaneView.Collapsed
      ? `${MILESTONES_COLLAPSED_COLUMN} ${PROJECT_CONTENT_COLUMN}`
      : `${MILESTONES_EXPANDED_COLUMN} ${PROJECT_CONTENT_COLUMN}`};
  height: 100vh;
  min-height: 0;
  transition: grid-template-columns 0.2s ease-out;
  width: 100%;
`;

export const AccessDeniedPage = styled.main`
  align-items: center;
  background: ${colors.white};
  display: flex;
  height: 100vh;
  justify-content: center;
  padding: ${dimensions.spacing["4"]};
  width: 100%;
`;

export const AccessDeniedContent = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${dimensions.spacing["2"]};
  max-width: ${ACCESS_DENIED_MAX_WIDTH};
  text-align: center;
`;

export const AccessDeniedIconFrame = styled.div`
  align-items: center;
  background: ${colors["grey-100"]};
  border-radius: 50%;
  color: ${colors["grey-600"]};
  display: flex;
  height: ${dimensions.spacing["6"]};
  justify-content: center;
  width: ${dimensions.spacing["6"]};
`;

export const AccessDeniedTitle = styled(Text).attrs({
  as: "h1",
  variant: "defaultHeader",
})`
  color: ${colors["grey-700"]};
  margin: 0;
`;

export const AccessDeniedDescription = styled(Text).attrs({
  as: "p",
  variant: "lead",
})`
  color: ${colors["grey-600"]};
  margin: 0 0 ${dimensions.spacing["1"]};
`;

export const AccessDeniedAction = styled(MauiButton)`
  && {
    border-radius: ${dimensions["border-radius"].md};
  }
`;

export const ProjectLoadingState = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex: 1;
  justify-content: center;
  min-height: 0;
  width: 100%;
`;

export const ProjectContentPane = styled.section`
  background: ${colors.white};
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  position: relative;
`;

export const ProjectContentToolbar = styled.div<{
  $showPreviewSurface: boolean;
  $toolbarPlacement: RaasProjectToolbarPlacement;
}>`
  align-items: center;
  background: ${({ $showPreviewSurface }) =>
    $showPreviewSurface ? PREVIEW_HEADER_BACKGROUND : colors.white};
  box-sizing: border-box;
  box-shadow: none;
  display: grid;
  flex: 0 0 auto;
  gap: ${({ $showPreviewSurface }) =>
    $showPreviewSurface
      ? dimensions["border-radius"].xs
      : dimensions.spacing["1"]};
  grid-template-areas: ${({ $toolbarPlacement }) =>
    $toolbarPlacement === RaasProjectToolbarPlacement.BelowTitle
      ? `"title actions"`
      : `"title toolbar actions"`};
  grid-template-columns: ${({ $toolbarPlacement }) =>
    $toolbarPlacement === RaasProjectToolbarPlacement.BelowTitle
      ? "minmax(0, 1fr) auto"
      : "auto minmax(0, 1fr) auto"};
  min-height: auto;
  padding: ${({ $showPreviewSurface }) =>
    $showPreviewSurface
      ? `${PREVIEW_HEADER_VERTICAL_PADDING} ${dimensions.spacing["3"]}`
      : `${dimensions.spacing["3"]} ${dimensions.spacing["3"]} ${dimensions.spacing["1"]}`};
  position: relative;
  z-index: 1;
`;

export const ProjectContentViewport = styled.div<{
  $hasGlobalArtifact?: boolean;
  $showPreviewSurface: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex: 1;
  background: ${({ $showPreviewSurface }) =>
    $showPreviewSurface
      ? PREVIEW_SURFACE_BACKGROUND
      : colors.white};
  min-height: 0;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: ${({ $hasGlobalArtifact, $showPreviewSurface }) => {
    const horizontalPadding = $hasGlobalArtifact
      ? CHAT_WITH_ARTIFACT_HORIZONTAL_PADDING
      : dimensions.spacing["3"];

    return $showPreviewSurface
      ? `${dimensions.spacing["2"]} ${horizontalPadding} 0`
      : `0 ${horizontalPadding} ${dimensions.spacing["3"]}`;
  }};
  position: relative;
`;

export const ProjectContentViewportToolbar = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
  min-width: 0;
  padding: ${dimensions.spacing["1"]} 0 ${dimensions.spacing["2"]};
  width: 100%;
`;

export const ProjectContentViewportBody = styled.div<{
  $hasGlobalArtifact?: boolean;
  $isResizingGlobalArtifact?: boolean;
  $primaryPaneWidthPercent?: number;
}>`
  cursor: ${({ $isResizingGlobalArtifact }) =>
    $isResizingGlobalArtifact ? "col-resize" : "auto"};
  display: grid;
  flex: 1;
  gap: ${({ $hasGlobalArtifact }) =>
    $hasGlobalArtifact ? dimensions.spacing["3"] : "0"};
  grid-template-areas: ${({ $hasGlobalArtifact }) =>
    $hasGlobalArtifact ? `"primary resize artifact"` : `"primary"`};
  grid-template-columns: ${({ $hasGlobalArtifact }) =>
    $hasGlobalArtifact
      ? `clamp(${RAAS_SPLIT_PANE_CHAT_MIN_WIDTH}, var(--raas-global-artifact-primary-width), ${RAAS_DELIVERABLE_CONTENT_MAX_WIDTH}) ${RAAS_SPLIT_PANE_RESIZE_HANDLE_WIDTH} minmax(${RAAS_SPLIT_PANE_ARTIFACT_MIN_WIDTH}, 1fr)`
      : "minmax(0, 1fr)"};
  min-height: 0;
  min-width: 0;
  --raas-global-artifact-primary-width: ${({ $primaryPaneWidthPercent = 57 }) =>
    `${$primaryPaneWidthPercent}%`};
  ${({ $hasGlobalArtifact }) =>
    $hasGlobalArtifact
      ? `
        --raas-deliverable-content-column-override: 1 / -1;
        --raas-deliverable-content-max-width-override: ${RAAS_DELIVERABLE_CONTENT_MAX_WIDTH};
      `
      : ""}

  @media (max-width: ${BreakpointsMap.md}px) {
    grid-template-areas: ${({ $hasGlobalArtifact }) =>
    $hasGlobalArtifact
      ? `"primary"
           "artifact"`
      : `"primary"`};
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: ${({ $hasGlobalArtifact }) =>
    $hasGlobalArtifact
      ? `minmax(0, 1fr) ${GLOBAL_ARTIFACT_MOBILE_ROW}`
      : "minmax(0, 1fr)"};
  }
`;

export const ProjectContentViewportPrimary = styled.div`
  display: flex;
  grid-area: primary;
  min-height: 0;
  min-width: 0;
`;

export const ProjectGlobalArtifactResizeHandle = styled(SplitPaneResizeHandle)``;

export const ProjectGlobalArtifactPane = styled.aside`
  --raas-deliverable-content-column-override: 1 / -1;
  --raas-deliverable-content-max-width-override: none;

  display: flex;
  grid-area: artifact;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`;

export const ClientDeliverableFrame = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${dimensions.spacing["2"]};
  min-height: 0;
  min-width: 0;
  padding: ${dimensions.spacing["2"]} ${dimensions.spacing["3"]} 0;
`;

export const ProjectContentTitleGroup = styled.div<{
  $showPreviewSurface: boolean;
}>`
  align-items: center;
  display: flex;
  gap: ${({ $showPreviewSurface }) =>
    $showPreviewSurface
      ? dimensions["border-radius"].sm
      : dimensions.spacing["1"]};
  grid-area: title;
  min-width: 0;
`;

export const ProjectContentTitle = styled(Text).attrs({
  as: "h1",
  variant: "defaultHeader",
}) <{
  $showPreviewSurface: boolean;
}>`
  color: ${colors["grey-700"]};
  font-size: ${({ $showPreviewSurface }) =>
    $showPreviewSurface
      ? PREVIEW_TITLE_FONT_SIZE
      : typography.header.defaultHeader["font-size"]};
  font-weight: ${({ $showPreviewSurface }) =>
    $showPreviewSurface
      ? 500
      : typography.header.defaultHeader["font-weight"]};
  line-height: ${({ $showPreviewSurface }) =>
    $showPreviewSurface
      ? PREVIEW_TITLE_LINE_HEIGHT
      : typography.header.defaultHeader["line-height"]};
  margin: 0;
  overflow: hidden;
  padding: ${DENSE_CONTROL_PADDING} 0;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ProjectStatusBadge = styled(Text).attrs({
  as: "span",
  variant: "detail",
}) <{
  $showPreviewSurface: boolean;
  $tone: "blue" | "green" | "grey" | "yellow";
}>`
  align-items: center;
  background: ${({ $tone }) => {
    if ($tone === "green") return colors["green-100"];
    if ($tone === "blue") return colors["blue-100"];
    if ($tone === "yellow") return colors["orange-100"];
    return colors["grey-150"];
  }};
  border-radius: ${dimensions.spacing["10"]};
  color: ${({ $tone }) => {
    if ($tone === "green") return colors["green-400"];
    if ($tone === "blue") return colors["blue-400"];
    if ($tone === "yellow") return colors["orange-400"];
    return colors["grey-600"];
  }};
  display: inline-flex;
  font-size: ${({ $showPreviewSurface }) =>
    $showPreviewSurface
      ? PREVIEW_STATUS_FONT_SIZE
      : typography.text.detail["font-size"]};
  font-weight: ${({ $showPreviewSurface }) =>
    $showPreviewSurface
      ? typography.text.default["font-weight"]
      : typography.text.detail["font-weight"]};
  line-height: ${({ $showPreviewSurface }) =>
    $showPreviewSurface
      ? PREVIEW_STATUS_LINE_HEIGHT
      : typography.text.data["line-height"]};
  min-height: ${({ $showPreviewSurface }) =>
    $showPreviewSurface
      ? PREVIEW_STATUS_MIN_HEIGHT
      : dimensions.spacing["3"]};
  padding: 0
    ${({ $showPreviewSurface }) =>
    $showPreviewSurface
      ? "6px"
      : dimensions["border-radius"].sm};
  white-space: nowrap;
`;

export const ProjectContentActions = styled.div`
  align-items: center;
  display: flex;
  gap: ${dimensions.spacing["1"]};
  grid-area: actions;
  justify-content: flex-end;
  max-width: 100%;
  min-width: 0;
`;

export const DeliverablesPanelTriggerButton = styled(MauiButton) <{
  $active?: boolean;
  $hasLabel?: boolean;
}>`
  && {
    align-items: center;
    background: ${({ $active }) =>
    $active ? colors["blue-100"] : colors["grey-150"]} !important;
    border: 0 !important;
    border-radius: ${({ $hasLabel }) =>
    $hasLabel ? dimensions.spacing["10"] : "50%"};
    box-shadow: none;
    color: ${({ $active }) =>
    $active ? colors["blue-400"] : colors["grey-600"]} !important;
    display: inline-flex;
    gap: ${({ $hasLabel }) =>
    $hasLabel ? dimensions["border-radius"].sm : "0"};
    height: ${dimensions.spacing["5"]};
    justify-content: center;
    max-width: ${({ $hasLabel }) =>
    $hasLabel ? "min(260px, 28vw)" : dimensions.spacing["5"]};
    min-width: 0;
    padding: ${({ $hasLabel }) =>
    $hasLabel ? `0 ${dimensions.spacing["1"]}` : "0"};
    width: ${({ $hasLabel }) =>
    $hasLabel ? "auto" : dimensions.spacing["5"]};
  }

  &&:focus {
    background: ${({ $active }) =>
    $active ? colors["blue-100"] : colors["grey-150"]} !important;
    color: ${({ $active }) =>
    $active ? colors["blue-400"] : colors["grey-600"]} !important;
  }

  &&:hover {
    background: ${({ $active }) =>
    $active ? colors["blue-100"] : colors["grey-200"]} !important;
    color: ${({ $active }) =>
    $active ? colors["blue-400"] : colors["grey-700"]} !important;
  }

  &&:focus-visible {
    background: ${colors["blue-100"]} !important;
    color: ${colors["blue-400"]} !important;
    outline: 2px solid ${colors["blue-300"]};
    outline-offset: 2px;
  }
`;

export const ProjectHeaderProjectName = styled.span`
  color: inherit;
  display: block;
  flex: 1 1 auto;
  font-size: ${typography.text.default["font-size"]};
  font-weight: ${typography.text.default["font-weight"]};
  line-height: ${typography.text.default["line-height"]};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CommentsPanelTriggerIcon = styled(Icon)`
  && {
    font-size: 18px !important;
    line-height: 18px;
    transform: scaleX(-1);
  }
`;

export const ProjectContentToolbarSlot = styled.div<{
  $toolbarPlacement: RaasProjectToolbarPlacement;
}>`
  grid-area: toolbar;
  justify-self: ${({ $toolbarPlacement }) =>
    $toolbarPlacement === RaasProjectToolbarPlacement.BelowTitle
      ? "center"
      : "stretch"};
  min-width: 0;
`;

export const ArtifactBucketRail = styled.div`
  align-items: center;
  display: flex;
  gap: ${dimensions["border-radius"].sm};
  min-width: 0;
  overflow-x: auto;
`;

export const ArtifactBucketButton = styled(MauiButton) <{
  $active?: boolean;
  $dashed?: boolean;
}>`
  && {
    align-items: center;
    background: ${({ $active }) =>
    $active ? colors["blue-100"] : colors.white} !important;
    border: 1px ${({ $dashed }) => ($dashed ? "dashed" : "solid")}
      ${({ $active }) =>
    $active ? colors["blue-100"] : colors["grey-300"]} !important;
    border-radius: ${dimensions["border-radius"].md};
    box-shadow: none;
    color: ${colors["grey-700"]} !important;
    display: inline-flex;
    font-size: ${typography.text.default["font-size"]};
    gap: ${dimensions["border-radius"].sm};
    height: ${dimensions.spacing["5"]};
    line-height: ${typography.text.default["line-height"]};
    padding: 0 ${dimensions.spacing["1"]};
    white-space: nowrap;
  }

  &&:hover,
  &&:focus {
    background: ${({ $active }) =>
    $active ? colors["blue-100"] : colors["grey-150"]} !important;
    color: ${colors["grey-700"]} !important;
  }

  && span {
    margin-left: 0 !important;
  }
`;

export const ArtifactBucketCount = styled.span`
  background: ${colors["blue-100"]};
  border-radius: ${dimensions["border-radius"].sm};
  color: ${colors["blue-400"]};
  padding: 0 ${dimensions["border-radius"].sm};
`;

export const ApproveButton = styled(MauiButton)`
  && {
    border-radius: ${dimensions.spacing["10"]};
    font-size: ${typography.text.default["font-size"]};
    height: ${dimensions.spacing["5"]};
    line-height: ${typography.text.default["line-height"]};
    padding: 0 ${dimensions.spacing["2"]};
  }

  &&:disabled,
  &&:disabled:hover {
    background: ${colors["blue-200"]} !important;
    border-color: ${colors["blue-200"]} !important;
    color: ${colors.white} !important;
    cursor: default;
    opacity: 1;
  }
`;

export const SubmitToClientSplitGroup = styled.div`
  border-radius: ${dimensions.spacing["10"]};
  box-shadow: ${ACTION_BUTTON_SHADOW};
  display: inline-flex;
  overflow: hidden;
`;

export const SubmitToClientButton = styled(MauiButton)`
  && {
    border-radius: 0;
    box-shadow: none;
    font-size: ${typography.text.default["font-size"]};
    font-weight: ${typography.text.label["font-weight"]};
    height: ${dimensions.height.sm};
    line-height: ${typography.text.default["line-height"]};
    padding: 0 12px;
    white-space: nowrap;
  }
`;

export const SubmitToClientDropdownCaret = styled(MauiButton)<{
  $standalone?: boolean;
}>`
  && {
    border-left: ${({ $standalone }) =>
      $standalone ? 0 : `1px solid ${colors["grey-300"]}`};
    border-radius: 0;
    box-shadow: none;
    height: ${dimensions.height.sm};
    min-width: 0;
    padding: 0 10px;
  }
`;

export const PreviewClientButton = styled(MauiButton)`
  && {
    align-items: center;
    background: ${colors.white} !important;
    border: 1px solid ${colors["grey-300"]} !important;
    border-radius: ${dimensions.spacing["10"]};
    box-shadow: ${ACTION_BUTTON_SHADOW};
    color: ${colors["grey-700"]} !important;
    display: inline-flex;
    font-size: ${typography.text.default["font-size"]};
    font-weight: ${typography.text.label["font-weight"]};
    gap: ${dimensions["border-radius"].sm};
    height: ${dimensions.height.sm};
    line-height: ${typography.text.default["line-height"]};
    padding: 0 12px;
    white-space: nowrap;
  }

  &&:hover,
  &&:focus {
    background: ${colors["grey-150"]} !important;
    color: ${colors["grey-700"]} !important;
  }

  && span {
    margin-left: 0 !important;
  }
`;

export const IntakeFlowShell = styled(DeliverableGridFrame)`
  box-sizing: border-box;
  grid-template-rows: minmax(0, 1fr);
  padding: 0;

  > * {
    grid-column: var(--raas-deliverable-content-column);
    grid-row: 1;
    height: 100% !important;
    justify-self: center;
    max-width: var(--raas-deliverable-content-max-width);
    min-height: 0;
    width: 100% !important;
  }
`;

export const ArtifactStubCard = styled.article`
  border: 1px solid ${colors["grey-300"]};
  border-radius: ${dimensions["border-radius"].lg};
  box-shadow: ${ARTIFACT_CARD_SHADOW};
  overflow: hidden;
`;

export const ArtifactDocumentFrame = styled(DeliverableGridFrame)<{
  $flush?: boolean;
}>`
  align-items: stretch;
  grid-template-rows: minmax(0, 1fr);
  padding: ${({ $flush }) =>
    $flush
      ? "0"
      : `${dimensions.spacing["4"]} ${dimensions.spacing["2"]} ${dimensions.spacing["2"]}`};
`;

export const ArtifactDocumentCard = styled(ArtifactStubCard)`
  display: flex;
  flex-direction: column;
  grid-column: var(--raas-deliverable-content-column);
  height: 100%;
  justify-self: center;
  max-width: var(--raas-deliverable-content-max-width);
  min-height: 0;
  min-width: 0;
  width: 100%;
`;

export const ArtifactStubHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid ${colors["grey-300"]};
  display: flex;
  justify-content: space-between;
  padding: ${dimensions.spacing["1"]} ${dimensions.spacing["2"]};
`;

export const ArtifactIconTitle = styled.div`
  align-items: center;
  color: ${colors["grey-700"]};
  display: flex;
  gap: ${dimensions.spacing["1"]};
  min-width: 0;

  > span {
    color: ${colors["blue-300"]};
    display: inline-flex;
    flex: 0 0 auto;
  }
`;

export const ArtifactStubTitle = styled(Text).attrs({
  as: "h2",
  variant: "defaultHeader",
})`
  color: ${colors["grey-700"]};
  font-size: ${ARTIFACT_TITLE_FONT_SIZE};
  line-height: ${ARTIFACT_TITLE_LINE_HEIGHT};
  margin: 0;
`;

export const ArtifactDocumentBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: ${dimensions.spacing["3"]};
`;

export const ArtifactMarkdownContent = styled.div`
  color: ${colors["grey-700"]};

  ul {
    margin: ${dimensions["border-radius"].sm} 0 ${dimensions.spacing["2"]};
    padding-left: ${dimensions.spacing["3"]};
  }

  li {
    margin: ${dimensions["border-radius"].xs} 0;
  }
`;

export const ArtifactMarkdownTitle = styled(Text).attrs({
  as: "h1",
  variant: "defaultHeader",
})`
  color: ${colors["grey-700"]};
  font-size: ${ARTIFACT_HEADING_FONT_SIZE};
  line-height: ${ARTIFACT_HEADING_LINE_HEIGHT};
  margin: 0;
`;

export const ArtifactMarkdownSectionTitle = styled(Text).attrs({
  as: "h2",
  variant: "lead",
})`
  border-top: 1px solid ${colors["grey-300"]};
  color: ${colors["grey-700"]};
  font-weight: ${typography.header.defaultHeader["font-weight"]};
  line-height: ${typography.text.lead["line-height"]};
  margin: ${dimensions.spacing["3"]} 0 0;
  padding-top: ${dimensions.spacing["2"]};
`;

export const ArtifactMarkdownSubsectionTitle = styled(Text).attrs({
  as: "h3",
  variant: "default",
})`
  color: ${colors["grey-700"]};
  font-size: ${COMPACT_SUBTITLE_FONT_SIZE};
  font-weight: ${typography.header.defaultHeader["font-weight"]};
  line-height: ${typography.text.data["line-height"]};
  margin: ${dimensions.spacing["2"]} 0 0;
`;

export const ArtifactMarkdownText = styled(Text).attrs({
  as: "p",
  variant: "default",
})`
  color: ${colors["grey-700"]};
  line-height: ${typography.text.data["line-height"]};
  margin: ${dimensions["border-radius"].sm} 0;
`;

export const ArtifactMarkdownStrong = styled(Text).attrs({
  as: "strong",
  variant: "default",
})`
  color: inherit;
  font-weight: ${typography.text.label["font-weight"]};
  line-height: inherit;
`;

export const ArtifactEmptyState = styled(Text).attrs({
  variant: "default",
})`
  align-items: center;
  color: ${colors["grey-500"]};
  display: flex;
  justify-content: center;
  min-height: ${ARTIFACT_EMPTY_MIN_HEIGHT};
`;

export const ArtifactStubBody = styled.div`
  display: grid;
`;

export const ArtifactInsightGrid = styled.div`
  display: grid;
  gap: ${dimensions.spacing["1"]};
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

export const ArtifactInsight = styled.div`
  border: 1px solid ${colors["grey-300"]};
  border-radius: ${dimensions["border-radius"].lg};
  padding: ${dimensions.spacing["2"]};

  h3 {
    color: ${colors["grey-500"]};
    font-size: ${typography.text.default["font-size"]};
    font-weight: ${typography.text.label["font-weight"]};
    line-height: ${typography.text.default["line-height"]};
    margin: 0 0 ${dimensions["border-radius"].sm};
  }

  p {
    color: ${colors["grey-700"]};
    font-size: ${typography.text.default["font-size"]};
    line-height: ${typography.text.default["line-height"]};
    margin: 0;
  }
`;

export const ArtifactActions = styled.div`
  align-items: center;
  display: flex;
  gap: ${dimensions["border-radius"].sm};
`;

export const ArtifactActionButton = styled(MauiButton)`
  && {
    align-items: center;
    background: ${colors.white} !important;
    border: 1px solid ${colors["grey-300"]} !important;
    border-radius: ${dimensions["border-radius"].md};
    box-shadow: none;
    color: ${colors["grey-700"]} !important;
    display: inline-flex;
    font-size: ${typography.text.default["font-size"]};
    gap: ${dimensions["border-radius"].sm};
    height: ${dimensions.spacing["5"]};
    line-height: ${typography.text.default["line-height"]};
    padding: 0 ${dimensions.spacing["1"]};
    white-space: nowrap;
  }

  &&:hover,
  &&:focus {
    background: ${colors["grey-150"]} !important;
    color: ${colors["grey-700"]} !important;
  }

  && span {
    margin-left: 0 !important;
  }
`;

export const ArtifactStubTable = styled.table`
  border-collapse: collapse;
  width: 100%;

  th,
  td {
    border-bottom: 1px solid ${colors["grey-300"]};
    color: ${colors["grey-700"]};
    font-size: ${typography.text.default["font-size"]};
    line-height: ${typography.text.default["line-height"]};
    padding: ${dimensions.spacing["1"]};
    text-align: left;
  }

  th {
    color: ${colors["grey-500"]};
    font-weight: ${typography.text.label["font-weight"]};
  }
`;

export const ArtifactTableScroller = styled.div`
  overflow-x: auto;
`;

export const RemoveCompanyButton = styled(MauiButton)`
  && {
    align-items: center;
    background: ${colors.white} !important;
    border: 1px solid ${colors["grey-300"]} !important;
    border-radius: ${dimensions["border-radius"].md};
    color: ${colors["grey-600"]} !important;
    display: inline-flex;
    height: ${dimensions.spacing["4"]};
    justify-content: center;
    padding: 0 !important;
    width: ${dimensions.spacing["4"]};
  }

  &&:hover,
  &&:focus {
    background: ${colors["grey-150"]} !important;
    color: ${colors["grey-700"]} !important;
  }
`;
